<script setup lang="ts">
import OzOverlay from '@@/library/v4/components/OzOverlay.vue'
import { nextTick, onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     * Current windowHeight, used to compute height-related measurement
     */
    windowHeight: number
    fullSize?: boolean
    /**
     * Desktop modal minimum height
     * If number is provided, unit of height will be in px e.g. :min-height="100" -> minHeight will be 100px
     * If string is provided, unit must be provided e.g. :min-height="'100rem'" -> minHeight will be 100rem
     */
    minHeight?: number | string
    /**
     * Starting click location based relative to window. To achieve zoom from click animation
     *
     * Type signature is `{ top: number; left: number }`
     */
    startingClickLocation?: { top: number; left: number } | undefined
    /**
     * Tailwindcss height class
     *
     * - `h-auto` is the default
     * - `h-90` is for the inline collaborator subpanel on the share panel for mobile web
     * - `h-*` is when we need to pass a custom height Tailwind class
     */
    heightClass?:
      | 'h-auto'
      | 'h-90'
      | (`h-${string}` & {}) /* Use `& {}` to narrow the type. TS widens union types by default. */
    /**
     *  Tailwindcss width class
     *
     *  - `w-180` is for the content picker
     *  - `w-135` is for poll builder modal
     *  - `w-128` is for the reaction edit modal
     *  - `w-101` is for the screen recorder modal
     *  - `w-82` is the default for all screens -- 328px = 360px - (16px * 2)
     *
     *  This allows us to use the same width for all devices.
     */
    widthClass?: 'w-180' | 'w-135' | 'w-128' | 'w-101' | 'w-82'
    /**
     *  Tailwindcss shadow class
     *
     *  - `shadow-z5` is the default
     *  - `shadow-elevation-4` is for the content picker when using new shadows
     */
    shadowClass?: 'shadow-z5' | 'shadow-elevation-4' | 'shadow-elevation-3'
    darkMode?: boolean | 'auto'
    shouldFadeIn?: boolean
    /**
     * Whether the `body` slot should be scrollable or not.
     * @default true
     */
    isBodyScrollable?: boolean
    scrollbarPreset?: OzCenterModalScrollbarPreset
  }>(),
  {
    fullSize: undefined,
    minHeight: 0,
    startingClickLocation: undefined,
    heightClass: 'h-auto',
    widthClass: 'w-180',
    shadowClass: 'shadow-z5',
    darkMode: 'auto',
    shouldFadeIn: true,
    isBodyScrollable: true,
    scrollbarPreset: OzCenterModalScrollbarPreset.Regular,
  },
)

defineEmits(['scrim-click', 'scrim-esc'])

const modalContainer = ref<HTMLDivElement>()
const minRenderHeight = ref('0px')

watch(
  [() => props.minHeight, () => props.fullSize, () => props.windowHeight],
  ([minHeight, fullSize, windowHeight]) => {
    // For tablet/desktop, we simply change the modal height
    nextTick(() => {
      if (fullSize) {
        minRenderHeight.value = windowHeight - 112 + 'px'
      } else {
        if (typeof minHeight === 'number') {
          minRenderHeight.value = minHeight + 'px'
        } else if (typeof minHeight === 'string') {
          minRenderHeight.value = minHeight
        }
      }
    })
  },
  { immediate: true },
)

const animateIn = (): void => {
  const modalContainerEl = modalContainer.value
  if (!props.startingClickLocation) {
    // we have to check separately like this so our old vue-tsc can infer props.startingClickLocation is not nullish after this block
    if (modalContainerEl) {
      modalContainerEl.style.animationName = 'modal-fade' // animation-name: modal-fade;
    }
    return
  }

  const centerOfScreen = {
    top: props.windowHeight / 2,
    left: window.innerWidth / 2,
  }
  const startOffset = {
    top: props.startingClickLocation.top - centerOfScreen.top,
    left: props.startingClickLocation.left - centerOfScreen.left,
  }
  const startTransformation = {
    scale: 0,
    translate3d: {
      y: startOffset.top,
      x: startOffset.left,
      z: 0,
    },
  }
  const endTransformation = {
    scale: 1,
    translate3d: { x: 0, y: 0, z: 0 },
  }

  function generateTransformString(details: {
    scale: number
    translate3d: { x: number; y: number; z: number }
  }): string {
    return `translate3d(${details.translate3d.x}px, ${details.translate3d.y}px, ${details.translate3d.z}px) scale(${details.scale}) `
  }

  if (modalContainerEl) {
    modalContainerEl.style.transition = 'none'
    modalContainerEl.style.transformOrigin = 'center'
    modalContainerEl.style.transform = generateTransformString(startTransformation)
    setTimeout(() => {
      /**
       * Decelerated easing for incoming element
       * @see https://material.io/design/motion/speed.html#easing
       */
      modalContainerEl.style.transition = 'all 300ms cubic-bezier(0.0, 0.0, 0.2, 1)'
      modalContainerEl.style.transform = generateTransformString(endTransformation)
    }, 1)
  }
}

onMounted((): void => {
  animateIn()
})
</script>

<script lang="ts">
export enum OzCenterModalScrollbarPreset {
  Regular = 'Regular',
  Thin = 'Thin',
}

export default {}
</script>

<template>
  <OzOverlay
    :class="[
      // Laying out the child container
      'flex',
      'items-center',
      'justify-center',
      // Can remove this once Oz page reset has been applied to all pages.
      'font-sans',
    ]"
    :should-fade-in="shouldFadeIn"
    :dark-mode="darkMode"
    @scrim-click="$emit('scrim-click')"
    @scrim-esc="$emit('scrim-esc')"
  >
    <div
      ref="modalContainer"
      :class="[
        // Margins
        'mt-0',
        // Layout
        'overflow-auto',
        'flex',
        'flex-col',
        // Main container's colors and flairs
        'bg-transparent',
        'rounded-3xl',
        'oz-modal-container-height',
        heightClass,
        widthClass,
        shadowClass,
      ]"
      :style="{ minHeight: minRenderHeight }"
    >
      <div
        :class="{
          // Colors for content
          'bg-light-ui-100 dark:bg-dark-ui-100': darkMode === 'auto',
          'bg-light-ui-100': darkMode === false,
          'bg-dark-ui-100': darkMode === true,
          // Layout wrt the main container
          'min-h-full': true,
          grow: true,
          'overflow-hidden': true,
          // Layout the children
          'flex-col': true,
          flex: true,
        }"
      >
        <header v-if="$slots.header">
          <slot name="header" />
        </header>
        <div
          :class="[
            'flex grow flex-col p-0',
            isBodyScrollable ? 'overflow-auto h-auto' : 'h-full',
            scrollbarPreset === OzCenterModalScrollbarPreset.Regular && {
              'scrollbar-regular': darkMode === false,
              'scrollbar-regular-dark': darkMode === true,
              'scrollbar-regular dark:scrollbar-regular-dark': darkMode === 'auto',
            },
            scrollbarPreset === OzCenterModalScrollbarPreset.Thin && {
              'scrollbar-thin': darkMode === false,
              'scrollbar-thin-dark': darkMode === true,
              'scrollbar-thin dark:scrollbar-thin-dark': darkMode === 'auto',
            },
          ]"
        >
          <slot name="body" />
        </div>
        <div v-if="$slots.footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </OzOverlay>
</template>

<style lang="scss" scoped>
@import '@@/styles/3/modules/responsive';
@import '@@/styles/3/modules/all';

.oz-modal-container-height {
  max-height: calc(100% - 112px); // 56px of padding on top and bottom of the dialog on desktop/tablet
  transition: min-height 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
}

.shadow-z5 {
  @include shadow-z5;
}
</style>
